// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Alert, Grid, Card, CardContent, Divider, Stack, Avatar } from '@mui/material';
import { AccessTime, Schedule } from '@mui/icons-material';
import { IconArrowDownRight } from '@tabler/icons-react';
import { useTheme } from '@mui/material/styles';
import Chart, { Props } from 'react-apexcharts';
import { useAuth } from '@/context/AuthContext';
import { apiService } from '@/services/api';
import ChildCard from '@/components/shared/ChildCard.tsx';
import DashboardCard from '@/components/shared/DashboardCard';
import { useTranslation } from '@/hooks/useTranslation';
import { useTranslation as useI18nTranslation } from 'react-i18next';
import icon2 from '../assets/images/svgs/icon-user-male.svg';
import icon3 from '../assets/images/svgs/icon-briefcase.svg';
import icon4 from '../assets/images/svgs/icon-mailbox.svg';
import icon5 from '../assets/images/svgs/icon-favorites.svg';
import icon6 from '../assets/images/svgs/icon-speech-bubble.svg';

interface ScheduleData {
  timezone: string;
  sunday: { start: string; end: string; isClosed: boolean };
  monday: { start: string; end: string; isClosed: boolean };
  tuesday: { start: string; end: string; isClosed: boolean };
  wednesday: { start: string; end: string; isClosed: boolean };
  thursday: { start: string; end: string; isClosed: boolean };
  friday: { start: string; end: string; isClosed: boolean };
  saturday: { start: string; end: string; isClosed: boolean };
}

interface DailyStatDto {
  date: string;
  value: number;
}

interface AdminDashboardResponseDto {
  balanceTopUpsByDay: DailyStatDto[];
  completedFilesByDay: DailyStatDto[];
  earnedCreditsByDay: DailyStatDto[];
  filesInProgress: number;
  activeCustomers: number;
  totalCompletedFiles: number;
  processedTickets: number;
  openTickets: number;
  eurTopUps7Days?: number;
  eurTopUps30Days?: number;
  eurTopUps365Days?: number;
}

interface CustomerDashboardResponseDto {
  completedFilesByDay: DailyStatDto[];
  spentCreditsByDay: DailyStatDto[];
  filesInProgress: number;
  openTickets: number;
}

interface cardType {
  icon: string;
  title: string;
  digits: string | number;
  bgcolor: string;
}

const Dashboard = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { i18n } = useI18nTranslation();
  const [notice, setNotice] = useState<any>(null);
  const [schedule, setSchedule] = useState<ScheduleData | null>(null);
  const [adminTime, setAdminTime] = useState<string>('');
  const [localTime, setLocalTime] = useState<string>('');
  const [dashboardData, setDashboardData] = useState<AdminDashboardResponseDto | CustomerDashboardResponseDto | null>(null);
  const [loadingDashboard, setLoadingDashboard] = useState<boolean>(false);

  useEffect(() => {
    const loadNotice = async () => {
      try {
        const data = await apiService.getNotice();
        if (data && data.isEnabled && user?.userType === 'customer') {
          // Перевіряємо чи notice не закінчився
          const expirationDate = new Date(data.expirationDate);
          const now = new Date();

          if (expirationDate > now) {
            // Get current language from user profile or i18n
            const currentLang = user?.language || i18n.language;
            // Get message for current language
            let message = '';
            switch (currentLang) {
              case 'en':
                message = data.messageEn || '';
                break;
              case 'de':
                message = data.messageDe || '';
                break;
              case 'ru':
                message = data.messageRu || '';
                break;
              case 'pl':
                message = data.messagePl || '';
                break;
              default:
                message = data.messageEn || '';
            }
            
            if (message) {
              setNotice({
                ...data,
                message,
              });
            } else {
              setNotice(null);
            }
          } else {
            setNotice(null);
          }
        } else {
          setNotice(null);
        }
      } catch (error) {
        console.error('Error loading notice:', error);
        setNotice(null);
      }
    };

    loadNotice();
  }, [user?.userType, user?.language, i18n.language]);

  // Load dashboard data
  useEffect(() => {
    const loadDashboard = async () => {
      setLoadingDashboard(true);
      try {
        const data = await apiService.getDashboard();
        if (data) {
          setDashboardData(data as AdminDashboardResponseDto | CustomerDashboardResponseDto | null);
        }
      } catch (error) {
        console.error('Error loading dashboard:', error);
      } finally {
        setLoadingDashboard(false);
      }
    };

    loadDashboard();
  }, []);

  // Load schedule for customer
  useEffect(() => {
    const loadSchedule = async () => {
      if (user?.userType === 'customer') {
        try {
          const data = await apiService.getSchedule();
          if (data) {
            setSchedule(data);
          }
        } catch (error) {
          console.error('Error loading schedule:', error);
        }
      }
    };

    loadSchedule();
  }, [user?.userType]);

  // Update time every second
  useEffect(() => {
    const formatTime = (hours: number, minutes: number, seconds: number): string => {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    const updateTime = () => {
      const now = new Date();
      
      // Admin time (Europe/Berlin)
      const berlinFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Europe/Berlin',
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
      const berlinParts = berlinFormatter.formatToParts(now);
      const berlinHours = parseInt(berlinParts.find(p => p.type === 'hour')?.value || '0', 10);
      const berlinMinutes = parseInt(berlinParts.find(p => p.type === 'minute')?.value || '0', 10);
      const berlinSeconds = parseInt(berlinParts.find(p => p.type === 'second')?.value || '0', 10);
      setAdminTime(formatTime(berlinHours, berlinMinutes, berlinSeconds));

      // Local time (user's timezone)
      const localHours = now.getHours();
      const localMinutes = now.getMinutes();
      const localSeconds = now.getSeconds();
      setLocalTime(formatTime(localHours, localMinutes, localSeconds));
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);

    return () => clearInterval(interval);
  }, []);

  const formatTime = (timeString: string): string => {
    if (!timeString) return '';
    
    // Try to parse different time formats
    // Format: "HH:MM" or "HH:MM:SS" or "HH:MM:SS.SSS"
    const timeMatch = timeString.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
    if (timeMatch) {
      const hours = parseInt(timeMatch[1], 10);
      const minutes = timeMatch[2];
      // Return in HH:MM format
      return `${hours.toString().padStart(2, '0')}:${minutes}`;
    }
    
    // If parsing fails, return original string
    return timeString;
  };

  const formatDaySchedule = (dayData: { start: string; end: string; isClosed: boolean }) => {
    if (dayData.isClosed) {
      return t('dashboard.openingHours.closed');
    }
    const formattedStart = formatTime(dayData.start);
    const formattedEnd = formatTime(dayData.end);
    return `${formattedStart} - ${formattedEnd}`;
  };

  const getCurrentDayName = (): string => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[new Date().getDay()];
  };

  const isToday = (dayName: string): boolean => {
    return dayName === getCurrentDayName();
  };

  // Generate topcards from dashboard data
  const getTopcards = (): cardType[] => {
    if (!dashboardData) return [];

    if (user?.userType === 'admin' && 'filesInProgress' in dashboardData) {
      const adminData = dashboardData as AdminDashboardResponseDto;
      return [
        {
          icon: icon3,
          title: t('dashboard.statistics.filesInProgress'),
          digits: adminData.filesInProgress || 0,
          bgcolor: 'info',
        },
        {
          icon: icon2,
          title: t('dashboard.statistics.activeCustomers'),
          digits: adminData.activeCustomers || 0,
          bgcolor: 'primary',
        },
        {
          icon: icon5,
          title: t('dashboard.statistics.totalCompletedFiles'),
          digits: adminData.totalCompletedFiles || 0,
          bgcolor: 'success',
        },
        {
          icon: icon4,
          title: t('dashboard.statistics.processedTickets'),
          digits: adminData.processedTickets || 0,
          bgcolor: 'secondary',
        },
        {
          icon: icon6,
          title: t('dashboard.statistics.openTickets'),
          digits: adminData.openTickets || 0,
          bgcolor: 'warning',
        },
      ];
    }

    if (user?.userType === 'customer') {
      const customerData = dashboardData as CustomerDashboardResponseDto;
      const totalCompleted = customerData.completedFilesByDay
        ? customerData.completedFilesByDay.reduce((sum, day) => sum + day.value, 0)
        : 0;
      return [
        {
          icon: icon3,
          title: t('dashboard.statistics.filesInProgress'),
          digits: customerData.filesInProgress || 0,
          bgcolor: 'primary',
        },
        {
          icon: icon4,
          title: t('dashboard.statistics.completedFilesLastWeek'),
          digits: totalCompleted,
          bgcolor: 'secondary',
        },
        {
          icon: icon6,
          title: t('dashboard.statistics.openTickets'),
          digits: customerData.openTickets || 0,
          bgcolor: 'error',
        },
      ];
    }

    return [];
  };

  const topcards = getTopcards();

  const theme = useTheme();
  const secondary = theme.palette.secondary.main;
  const secondarylight = theme.palette.secondary.light;
  const errorlight = theme.palette.error.light;

  const getBalanceTopUpsChartData = () => {
    if (!dashboardData || user?.userType !== 'admin' || !('balanceTopUpsByDay' in dashboardData)) {
      return { options: null, series: null };
    }

    const adminData = dashboardData as AdminDashboardResponseDto;
    const data = adminData.balanceTopUpsByDay || [];
    const chartData = data.map(item => item.value);
    const chartDates = data.map(item => {
      const date = new Date(item.date);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    });

    const optionscolumnchart: Props = {
      chart: {
        type: 'area',
        fontFamily: "'Plus Jakarta Sans', sans-serif;",
        foreColor: theme.palette.mode === 'dark' ? '#adb0bb' : '#5a6c7d',
        toolbar: { show: false },
        height: 80,
        sparkline: { enabled: true },
        group: 'sparklines',
      },
      stroke: { curve: 'smooth', width: 2 },
      fill: { colors: [secondarylight], type: 'solid', opacity: 0.4 },
      markers: { size: 0 },
      tooltip: {
        theme: theme.palette.mode === 'dark' ? 'dark' : 'light',
        style: { fontSize: '12px', fontFamily: "'Plus Jakarta Sans', sans-serif" },
        x: { show: false },
        fixed: { enabled: false },
        followCursor: true,
        shared: true,
        hideDelay: 0,
        intersect: false,
      },
      xaxis: { categories: chartDates },
    };

    const seriescolumnchart = [
      { name: t('dashboard.charts.balanceTopUps.seriesName'), color: secondary, data: chartData },
    ];

    return { options: optionscolumnchart, series: seriescolumnchart };
  };

  const balanceChart = useMemo(() => getBalanceTopUpsChartData(), [
    dashboardData,
    user?.userType,
    i18n.language,
    t,
    theme.palette.mode,
  ]);

  // Chart configuration for completedFilesByDay and earnedCreditsByDay
  const getFilesAndCreditsChartData = () => {
    if (!dashboardData || user?.userType !== 'admin' || !('completedFilesByDay' in dashboardData)) {
      return { options: null, series: null };
    }

    const adminData = dashboardData as AdminDashboardResponseDto;
    const completedFilesData = adminData.completedFilesByDay || [];
    const earnedCreditsData = adminData.earnedCreditsByDay || [];
    
    // Get dates from completedFilesByDay (assuming both have same dates)
    const chartDates = completedFilesData.map(item => {
      const date = new Date(item.date);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    });

    const completedFilesValues = completedFilesData.map(item => item.value);
    const earnedCreditsValues = earnedCreditsData.map(item => item.value);

    const primary = theme.palette.primary.main;
    const secondary = theme.palette.secondary.main;

    const optionslinechart: Props = {
      chart: {
        type: 'line',
        fontFamily: "'Plus Jakarta Sans', sans-serif;",
        foreColor: theme.palette.mode === 'dark' ? '#adb0bb' : '#5a6c7d',
        toolbar: {
          show: false,
        },
        height: 300,
      },
      stroke: {
        curve: 'smooth',
        width: 2,
      },
      fill: {
        type: 'solid',
        opacity: 0.4,
      },
      markers: {
        size: 4,
        hover: {
          size: 6,
        },
      },
      tooltip: {
        theme: theme.palette.mode === 'dark' ? 'dark' : 'light',
        style: {
          fontSize: '12px',
          fontFamily: "'Plus Jakarta Sans', sans-serif",
        },
        fixed: {
          enabled: false,
        },
        shared: true,
        hideDelay: 0,
        intersect: false,
      },
      xaxis: {
        categories: chartDates,
      },
      legend: {
        show: true,
        position: 'top',
      },
      colors: [primary, secondary],
    };

    const serieslinechart = [
      {
        name: t('dashboard.charts.filesAndCredits.completedFiles'),
        color: primary,
        data: completedFilesValues,
      },
      {
        name: t('dashboard.charts.filesAndCredits.earnedCredits'),
        color: secondary,
        data: earnedCreditsValues,
      },
    ];

    return { options: optionslinechart, series: serieslinechart };
  };

  const filesAndCreditsChart = useMemo(() => getFilesAndCreditsChartData(), [
    dashboardData,
    user?.userType,
    i18n.language,
    t,
    theme.palette.mode,
  ]);

  // Chart configuration for customer: completedFilesByDay and spentCreditsByDay
  const getCustomerFilesAndCreditsChartData = () => {
    if (!dashboardData || user?.userType !== 'customer' || !('completedFilesByDay' in dashboardData)) {
      return { options: null, series: null };
    }

    const customerData = dashboardData as CustomerDashboardResponseDto;
    const completedFilesData = customerData.completedFilesByDay || [];
    const spentCreditsData = customerData.spentCreditsByDay || [];
    
    // Get dates from completedFilesByDay (assuming both have same dates)
    const chartDates = completedFilesData.map(item => {
      const date = new Date(item.date);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    });

    const completedFilesValues = completedFilesData.map(item => item.value);
    const spentCreditsValues = spentCreditsData.map(item => item.value);

    const primary = theme.palette.primary.main;
    const secondary = theme.palette.secondary.main;

    const optionslinechart: Props = {
      chart: {
        type: 'line',
        fontFamily: "'Plus Jakarta Sans', sans-serif;",
        foreColor: theme.palette.mode === 'dark' ? '#adb0bb' : '#5a6c7d',
        toolbar: {
          show: false,
        },
        height: 300,
      },
      stroke: {
        curve: 'smooth',
        width: 2,
      },
      fill: {
        type: 'solid',
        opacity: 0.4,
      },
      markers: {
        size: 4,
        hover: {
          size: 6,
        },
      },
      tooltip: {
        theme: theme.palette.mode === 'dark' ? 'dark' : 'light',
        fixed: {
          enabled: false,
        },
        followCursor: true,
        shared: true,
        hideDelay: 0,
        intersect: false,
      },
      xaxis: {
        categories: chartDates,
      },
      legend: {
        show: true,
        position: 'top',
      },
      colors: [primary, secondary],
    };

    const serieslinechart = [
      {
        name: t('dashboard.charts.filesAndCredits.completedFiles'),
        color: primary,
        data: completedFilesValues,
      },
      {
        name: t('dashboard.charts.filesAndCredits.spentCredits'),
        color: secondary,
        data: spentCreditsValues,
      },
    ];

    return { options: optionslinechart, series: serieslinechart };
  };

  const customerFilesAndCreditsChart = useMemo(() => getCustomerFilesAndCreditsChartData(), [
    dashboardData,
    user?.userType,
    i18n.language,
    t,
    theme.palette.mode,
  ]);

  return (
    <Box sx={{ p: 3 }}>
      {notice && (
        <Alert
          severity={notice.type as 'success' | 'warning' | 'error'}
          variant="outlined"
          sx={{
            mb: 3,
            color: theme.palette.mode === 'dark' ? 'white' : '#333333',
            background: theme.palette.mode === 'dark' ? '#333333' : 'white',
            '& .MuiAlert-icon': {
              alignSelf: 'flex-start',
              mt: 0.5,
            },
          }}
        >
          <div dangerouslySetInnerHTML={{ __html: notice.message }} />
        </Alert>
      )}

      {/* Dashboard Statistics */}
      {dashboardData && !loadingDashboard && topcards.length > 0 && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {topcards.map((topcard, i) => (
            <Grid
              key={i}
              size={{
                xs: 12,
                sm: 4,
                lg: topcards.length > 4 ? 2.4 : topcards.length === 2 ? 6 : 4,
              }}
            >
              <DashboardCard>
                 <Box
                   component="img"
                   src={topcard.icon}
                   alt={topcard.title}
                   sx={{ width: 50, height: 50, margin: '0 auto', display: 'block' }}
                 />
                 <Typography variant="h6" sx={{ mt: 1, mb: 0.5, textAlign: 'center' }}>
                   {topcard.title}
                 </Typography>
                 <Typography sx={{ textAlign: 'center'}} variant="h4" fontWeight={600}>
                   {topcard.digits}
                 </Typography>
               </DashboardCard>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Balance Top Ups (70%) + New block (30%) - Admin only */}
      {user?.userType === 'admin' && dashboardData && 'balanceTopUpsByDay' in dashboardData && balanceChart.options && balanceChart.series && (
        <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
          <Box sx={{ flex: { xs: '1 1 100%', md: '0 0 calc(70% - 12px)' }, minWidth: 0 }}>
            <DashboardCard
              footer={
                <Chart
                  options={balanceChart.options}
                  series={balanceChart.series}
                  type="area"
                  height="80px"
                />
              }
            >
              <Typography variant="subtitle2" color="textSecondary">
                {t('dashboard.charts.balanceTopUps.title')}
              </Typography>
              <Typography variant="h4" fontWeight={600}>
                {dashboardData.balanceTopUpsByDay
                  ? dashboardData.balanceTopUpsByDay.reduce((sum, day) => sum + day.value, 0)
                  : 0}
              </Typography>
              <Stack direction="row" spacing={1} mt={1} alignItems="center">
                <Avatar sx={{ bgcolor: errorlight, width: 24, height: 24 }}>
                  <IconArrowDownRight width={18} color="#FA896B" />
                </Avatar>
                <Typography variant="subtitle2" fontWeight="600">
                  {t('dashboard.charts.balanceTopUps.last7Days')}
                </Typography>
              </Stack>
            </DashboardCard>
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', md: '0 0 calc(30% - 12px)' }, minWidth: 0, display: 'flex' }}>
            <Card
              variant="outlined"
              sx={{
                flex: 1,
                height: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                border: 1,
                borderColor: 'grey.200',
                '& .MuiCardContent-root:last-child': {
                  paddingBottom: 0,
                },
              }}
            >
              <CardContent sx={{ p: 0, flex: 1 }}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  {t('dashboard.charts.topUpsEur.title')}
                </Typography>
                <Stack spacing={0.5}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('dashboard.charts.topUpsEur.last7Days')}
                    </Typography>
                    <Typography variant="h6" fontWeight={600}>
                      €{((): string => {
                        const v = (dashboardData as AdminDashboardResponseDto)?.eurTopUps7Days;
                        return typeof v === 'number' ? v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
                      })()}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('dashboard.charts.topUpsEur.last30Days')}
                    </Typography>
                    <Typography variant="h6" fontWeight={600}>
                      €{((): string => {
                        const v = (dashboardData as AdminDashboardResponseDto)?.eurTopUps30Days;
                        return typeof v === 'number' ? v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
                      })()}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('dashboard.charts.topUpsEur.last365Days')}
                    </Typography>
                    <Typography variant="h6" fontWeight={600}>
                      €{((): string => {
                        const v = (dashboardData as AdminDashboardResponseDto)?.eurTopUps365Days;
                        return typeof v === 'number'
                          ? v.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                          : '—';
                      })()}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {/* Completed Files and Earned Credits Chart - Admin only */}
      {user?.userType === 'admin' && dashboardData && 'completedFilesByDay' in dashboardData && filesAndCreditsChart.options && filesAndCreditsChart.series && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard
              title={t('dashboard.charts.filesAndCredits.title')}
              footer={
                <>
                  <Chart
                    options={filesAndCreditsChart.options}
                    series={filesAndCreditsChart.series}
                    type="line"
                    height="300px"
                  />
                </>
              }
            >
              <>
                <Typography variant="subtitle2" color="textSecondary">
                  {t('dashboard.charts.filesAndCredits.subtitle')}
                </Typography>
              </>
            </DashboardCard>
          </Grid>
        </Grid>
      )}

      {/* Completed Files and Spent Credits Chart - Customer only */}
      {user?.userType === 'customer' && dashboardData && 'completedFilesByDay' in dashboardData && customerFilesAndCreditsChart.options && customerFilesAndCreditsChart.series && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard
              title={t('dashboard.charts.filesAndCredits.title')}
              footer={
                <>
                  <Chart
                    options={customerFilesAndCreditsChart.options}
                    series={customerFilesAndCreditsChart.series}
                    type="line"
                    height="300px"
                  />
                </>
              }
            >
              <>
                <Typography variant="subtitle2" color="textSecondary">
                  {t('dashboard.charts.filesAndCredits.subtitle')}
                </Typography>
              </>
            </DashboardCard>
          </Grid>
        </Grid>
      )}

      {user?.userType === 'customer' && (
        <Grid container spacing={3}>
          {/* Opening Hours Widget */}
          <Grid size={{ xs: 12, md: 6 }}>
            <ChildCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Schedule sx={{ color: 'primary.main', mr: 1 }} />
                  <Typography variant="h6" fontWeight={600}>
                    {t('dashboard.openingHours.title')}
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                {schedule ? (
                  <Box>
                    <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between' }}>
                      <Typography
                        variant="body1"
                        sx={{
                          mb: 0.5,
                          color: isToday('Monday') ? 'primary.main' : 'text.primary',
                          fontWeight: isToday('Monday') ? 600 : 500,
                        }}
                      >
                        {t('dashboard.openingHours.days.monday')}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: schedule.monday.isClosed ? 'error.main' : 'text.secondary',
                        }}
                      >
                        {formatDaySchedule(schedule.monday)}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between' }}>
                      <Typography
                        variant="body1"
                        sx={{
                          mb: 0.5,
                          color: isToday('Tuesday') ? 'primary.main' : 'text.primary',
                          fontWeight: isToday('Tuesday') ? 600 : 500,
                        }}
                      >
                        {t('dashboard.openingHours.days.tuesday')}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: schedule.tuesday.isClosed ? 'error.main' : 'text.secondary',
                        }}
                      >
                        {formatDaySchedule(schedule.tuesday)}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between' }}>
                      <Typography
                        variant="body1"
                        sx={{
                          mb: 0.5,
                          color: isToday('Wednesday') ? 'primary.main' : 'text.primary',
                          fontWeight: isToday('Wednesday') ? 600 : 500,
                        }}
                      >
                        {t('dashboard.openingHours.days.wednesday')}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: schedule.wednesday.isClosed ? 'error.main' : 'text.secondary',
                        }}
                      >
                        {formatDaySchedule(schedule.wednesday)}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between' }}>
                      <Typography
                        variant="body1"
                        sx={{
                          mb: 0.5,
                          color: isToday('Thursday') ? 'primary.main' : 'text.primary',
                          fontWeight: isToday('Thursday') ? 600 : 500,
                        }}
                      >
                        {t('dashboard.openingHours.days.thursday')}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: schedule.thursday.isClosed ? 'error.main' : 'text.secondary',
                        }}
                      >
                        {formatDaySchedule(schedule.thursday)}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between' }}>
                      <Typography
                        variant="body1"
                        sx={{
                          mb: 0.5,
                          color: isToday('Friday') ? 'primary.main' : 'text.primary',
                          fontWeight: isToday('Friday') ? 600 : 500,
                        }}
                      >
                        {t('dashboard.openingHours.days.friday')}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: schedule.friday.isClosed ? 'error.main' : 'text.secondary',
                        }}
                      >
                        {formatDaySchedule(schedule.friday)}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between' }}>
                      <Typography
                        variant="body1"
                        sx={{
                          mb: 0.5,
                          color: isToday('Saturday') ? 'primary.main' : 'text.primary',
                          fontWeight: isToday('Saturday') ? 600 : 500,
                        }}
                      >
                        {t('dashboard.openingHours.days.saturday')}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: schedule.saturday.isClosed ? 'error.main' : 'text.secondary',
                        }}
                      >
                        {formatDaySchedule(schedule.saturday)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography
                        variant="body1"
                        sx={{
                          mb: 0.5,
                          color: isToday('Sunday') ? 'primary.main' : 'text.primary',
                          fontWeight: isToday('Sunday') ? 600 : 500,
                        }}
                      >
                        {t('dashboard.openingHours.days.sunday')}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: schedule.sunday.isClosed ? 'error.main' : 'text.secondary',
                        }}
                      >
                        {formatDaySchedule(schedule.sunday)}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t('dashboard.openingHours.scheduleNotAvailable')}
                  </Typography>
                )}
              </CardContent>
            </ChildCard>
          </Grid>

          {/* Current Time Widget */}
          <Grid size={{ xs: 12, md: 6 }}>
            <ChildCard height="100%">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AccessTime sx={{ color: 'primary.main', mr: 1 }} />
                  <Typography variant="h6" fontWeight={600}>
                    {t('dashboard.currentTime.title')}
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {t('dashboard.currentTime.adminTime')}
                    </Typography>
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                      {adminTime}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {t('dashboard.currentTime.localTime', {
                        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                      })}
                    </Typography>
                    <Typography variant="h6" fontWeight={600}>
                      {localTime}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </ChildCard>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default Dashboard;
